import { api } from '@/lib/api';
import type { GroupFormData, GroupInvoiceAdjustment, LocalizedText } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface Group {
  id: string;
  projectId: string;
  name: LocalizedText;
  packagePrice: string;
  singleRoomSupplement: string | null;
  invoiceDescription: LocalizedText | null;
  depositDescription: LocalizedText | null;
  invoiceRemark: LocalizedText | null;
  invoiceAdjustments: GroupInvoiceAdjustment[] | null;
  registrationCount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: LocalizedText;
  };
}

export function useGroups(projectId?: string, includeInactive = false) {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (includeInactive) params.append('includeInactive', 'true');
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['groups', projectId, includeInactive],
    queryFn: () => api.get<Group[]>(`/groups${queryParams}`),
  });
}

export function useGroup(id: string) {
  return useQuery({
    queryKey: ['groups', id],
    queryFn: () => api.get<Group>(`/groups/${id}`),
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GroupFormData) => api.post<Group>('/groups', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Groep aangemaakt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij aanmaken groep: ${error.message}`);
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GroupFormData> }) => api.put<Group>(`/groups/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['groups', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Groep bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken groep: ${error.message}`);
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Groep verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen groep: ${error.message}`);
    },
  });
}
